import { useState } from 'react'
import { preview as platformPreview } from '@carrent/platform'
import { api } from '@carrent/api'
import { GeoStatus } from '@carrent/ui'

import { useGeoStatus } from '../../hooks/useGeoStatus'
import { useTripGeo } from '../../hooks/useTripGeo'

const EVENT_TYPES = [
  { value: 'refuel',      label: 'Заправка',  icon: '⛽', color: '#f59e0b' },
  { value: 'wash',        label: 'Мойка',     icon: '🫧', color: '#3b82f6' },
  { value: 'maintenance', label: 'ТО/Ремонт', icon: '🔧', color: '#ef4444' },
  { value: 'other',       label: 'Другое',    icon: '📝', color: '#6b7280' },
]

const FUEL_TYPES = [
  { value: 'gasoline', label: 'Бензин' },
  { value: 'diesel',   label: 'Дизель' },
  { value: 'gas',      label: 'Газ (LPG)' },
  { value: 'electric', label: 'Электро' },
]

const WASH_TYPES = [
  { value: 'manual',      label: 'Ручная' },
  { value: 'auto',        label: 'Автоматическая' },
  { value: 'contactless', label: 'Бесконтактная' },
  { value: 'other',       label: 'Другая' },
]

const MAINTENANCE_TYPES = [
  { value: 'oil_change',    label: 'Замена масла' },
  { value: 'tire_change',   label: 'Замена шин / колёс' },
  { value: 'brake_service', label: 'Тормоза' },
  { value: 'engine_repair', label: 'Ремонт двигателя' },
  { value: 'body_repair',   label: 'Кузовной ремонт' },
  { value: 'diagnostics',   label: 'Диагностика' },
  { value: 'inspection',    label: 'Техосмотр' },
  { value: 'other',         label: 'Другое' },
]

// ── Слот одного фото ──────────────────────────────────────────────────────────
function PhotoSlot({ label, hint, value, onChange, accent }) {
  const preview = value ? platformPreview.objectUrl(value) : null
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <label className="field__label" style={{ fontSize: '0.78rem', margin: 0 }}>{label}</label>
        {value && (
          <button type="button" onClick={() => onChange(null)}
            style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '0.8rem', padding: 0 }}>
            ✕ Удалить
          </button>
        )}
      </div>
      {preview ? (
        <div style={{ position: 'relative' }}>
          <img src={preview} alt={label}
            style={{ width: '100%', height: 110, objectFit: 'cover',
              borderRadius: 'var(--r-sm)', border: `2px solid ${accent || 'var(--success)'}` }} />
          <div style={{ position: 'absolute', bottom: 5, left: 8,
            background: 'rgba(34,197,94,0.9)', color: '#fff',
            padding: '2px 10px', borderRadius: 99, fontSize: '0.72rem', fontWeight: 700 }}>
            ✓ Фото сделано
          </div>
          <label style={{ position: 'absolute', top: 5, right: 8,
            background: 'rgba(0,0,0,0.65)', color: '#fff', borderRadius: 'var(--r-sm)',
            padding: '3px 9px', fontSize: '0.72rem', cursor: 'pointer' }}>
            🔄 Переснять
            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
              onChange={e => { onChange(e.target.files[0] || null); e.target.value = '' }} />
          </label>
        </div>
      ) : (
        <label style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          height: 76, border: `1px dashed ${accent || 'var(--border)'}`,
          borderRadius: 'var(--r-sm)', cursor: 'pointer',
          fontSize: '0.82rem', color: 'var(--text-muted)',
          background: accent ? `${accent}0d` : 'transparent',
        }}>
          <span style={{ fontSize: '1.3rem' }}>📷</span>
          {hint || 'Нажмите для съёмки'}
          <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
            onChange={e => { onChange(e.target.files[0] || null); e.target.value = '' }} />
        </label>
      )}
    </div>
  )
}

// ── Форма заправки ────────────────────────────────────────────────────────────
function RefuelForm({ form, set, photos, setPhoto, suggestedMileage }) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gap-sm)' }}>
        <div className="field" style={{ margin: 0 }}>
          <label className="field__label" style={{ fontSize: '0.78rem' }}>Тип топлива</label>
          <select className="input" value={form.fuel_type} onChange={set('fuel_type')}>
            {FUEL_TYPES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}>
          <label className="field__label" style={{ fontSize: '0.78rem' }}>Литры</label>
          <input className="input" type="number" step="0.1" placeholder="40"
            value={form.fuel_liters} onChange={set('fuel_liters')} />
        </div>
      </div>

      {/* Пробег по одометру — критичен для расчёта расхода */}
      <div className="field" style={{ margin: 0 }}>
        <label className="field__label" style={{ fontSize: '0.78rem' }}>
          Пробег по одометру (км)
          <span className="text-muted" style={{ fontWeight: 400, marginLeft: 4 }}>
            — для расчёта расхода
          </span>
        </label>
        <input className="input" type="number" placeholder={suggestedMileage?.toString() || ''}
          value={form.mileage_at_refuel} onChange={set('mileage_at_refuel')} />
        {suggestedMileage > 0 && !form.mileage_at_refuel && (
          <button type="button"
            onClick={() => set('mileage_at_refuel')({ target: { value: suggestedMileage.toString() } })}
            style={{
              background: 'none', border: 'none', color: 'var(--accent)',
              cursor: 'pointer', fontSize: '0.75rem', padding: '4px 0', textAlign: 'left',
            }}>
            ↳ Подставить {suggestedMileage.toLocaleString()} км
          </button>
        )}
      </div>

      {/* Чекбокс «до полного» — ключ к точному расчёту */}
      <button type="button"
        onClick={() => set('is_full_tank')({ target: { value: !form.is_full_tank } })}
        style={{
          padding: 'var(--gap-sm) var(--gap)',
          border: `2px solid ${form.is_full_tank ? '#f59e0b' : 'var(--border)'}`,
          borderRadius: 'var(--r)',
          background: form.is_full_tank ? 'rgba(245,158,11,0.08)' : 'var(--bg-card)',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
          transition: 'all 0.15s', textAlign: 'left',
        }}>
        <div style={{
          width: 22, height: 22, borderRadius: 6, flexShrink: 0,
          border: `2px solid ${form.is_full_tank ? '#f59e0b' : 'var(--border)'}`,
          background: form.is_full_tank ? '#f59e0b' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s',
        }}>
          {form.is_full_tank && <span style={{ color: '#fff', fontSize: '0.85rem' }}>✓</span>}
        </div>
        <div style={{ flex: 1 }}>
          <p style={{ fontWeight: 600, fontSize: '0.88rem',
            color: form.is_full_tank ? '#f59e0b' : 'var(--text)' }}>
            Заправил до полного бака
          </p>
          <p className="text-muted" style={{ fontSize: '0.72rem', marginTop: 1 }}>
            Помогает точно посчитать расход топлива
          </p>
        </div>
      </button>

      <div className="field" style={{ margin: 0 }}>
        <label className="field__label" style={{ fontSize: '0.78rem' }}>Название АЗС</label>
        <input className="input" placeholder="Лукойл, Газпромнефть..."
          value={form.station_name} onChange={set('station_name')} />
      </div>

      <div style={{ height: 1, background: 'var(--border)' }} />
      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.06em' }}>Фотофиксация</p>

      <PhotoSlot label="⛽ Машина на заправке"
        hint="Авто у колонки, видно марку топлива"
        value={photos.scene} onChange={f => setPhoto('scene', f)} accent="#f59e0b" />
      <PhotoSlot label="🔢 Одометр после заправки"
        hint="Приборная панель — текущий пробег"
        value={photos.odometer} onChange={f => setPhoto('odometer', f)} accent="#3b82f6" />
      <PhotoSlot label="🧾 Чек"
        hint="Чек с суммой и количеством литров"
        value={photos.receipt} onChange={f => setPhoto('receipt', f)} accent="#22c55e" />
    </>
  )
}

// ── Форма мойки ───────────────────────────────────────────────────────────────
function WashForm({ form, set, photos, setPhoto }) {
  return (
    <>
      <div className="field" style={{ margin: 0 }}>
        <label className="field__label" style={{ fontSize: '0.78rem' }}>Тип мойки</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {WASH_TYPES.map(wt => (
            <button key={wt.value} type="button"
              onClick={() => set('wash_type')({ target: { value: wt.value } })}
              style={{
                padding: '8px', border: `1px solid ${form.wash_type === wt.value ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 'var(--r-sm)', background: form.wash_type === wt.value ? 'rgba(59,130,246,0.12)' : 'transparent',
                cursor: 'pointer', fontSize: '0.82rem',
                color: form.wash_type === wt.value ? 'var(--accent)' : 'var(--text)',
                fontWeight: form.wash_type === wt.value ? 700 : 400,
                transition: 'all 0.15s',
              }}>
              {wt.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ height: 1, background: 'var(--border)' }} />
      <PhotoSlot label="🫧 Фото мойки / чека"
        hint="Машина на мойке или фото чека"
        value={photos.receipt} onChange={f => setPhoto('receipt', f)} accent="#3b82f6" />
    </>
  )
}

// ── Форма ТО / ремонта ────────────────────────────────────────────────────────
function MaintenanceForm({ form, set, photos, setPhoto }) {
  return (
    <>
      <div className="field" style={{ margin: 0 }}>
        <label className="field__label" style={{ fontSize: '0.78rem' }}>Вид работ</label>
        <select className="input" value={form.maintenance_type} onChange={set('maintenance_type')}>
          <option value="">— Выберите —</option>
          {MAINTENANCE_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
      </div>

      <div className="field" style={{ margin: 0 }}>
        <label className="field__label" style={{ fontSize: '0.78rem' }}>Название СТО / мастерской</label>
        <input className="input" placeholder="Название сервиса..."
          value={form.service_name} onChange={set('service_name')} />
      </div>

      <div className="field" style={{ margin: 0 }}>
        <label className="field__label" style={{ fontSize: '0.78rem' }}>Пробег при обслуживании (км)</label>
        <input className="input" type="number" placeholder="34000"
          value={form.mileage_at_service} onChange={set('mileage_at_service')} />
      </div>

      <div style={{ height: 1, background: 'var(--border)' }} />
      <PhotoSlot label="🔧 Фото акта / документа"
        hint="Акт выполненных работ, квитанция"
        value={photos.receipt} onChange={f => setPhoto('receipt', f)} accent="#ef4444" />
      <PhotoSlot label="📷 Фото работ"
        hint="Процесс или результат"
        value={photos.scene} onChange={f => setPhoto('scene', f)} accent="#6b7280" />
    </>
  )
}

// ── Форма «Другое» ────────────────────────────────────────────────────────────
function OtherForm({ photos, setPhoto }) {
  return (
    <>
      <div style={{ height: 1, background: 'var(--border)' }} />
      <PhotoSlot label="📎 Подтверждающее фото"
        hint="Любое фото по ситуации"
        value={photos.receipt} onChange={f => setPhoto('receipt', f)} />
    </>
  )
}

// ── Модальное окно событий ────────────────────────────────────────────────────
function EventModal({ tripId, onClose, onAdded, initialType, suggestedMileage }) {
  const [type, setType] = useState(initialType || 'refuel')
  const [form, setFormState] = useState({
    description: '', amount: '',
    fuel_type: 'gasoline', fuel_liters: '', station_name: '',
    mileage_at_refuel: '', is_full_tank: false,
    wash_type: 'manual',
    maintenance_type: '', service_name: '', mileage_at_service: '',
  })
  const [photos, setPhotos] = useState({ scene: null, odometer: null, receipt: null })
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  const set = k => e => setFormState(f => ({ ...f, [k]: e.target.value }))
  const setPhoto = (k, v) => setPhotos(p => ({ ...p, [k]: v }))

  const switchType = (t) => {
    setType(t)
    setPhotos({ scene: null, odometer: null, receipt: null })
    setErr('')
  }

  const submit = async () => {
    setLoading(true); setErr('')
    const fd = new FormData()
    fd.append('event_type', type)
    if (form.description) fd.append('description', form.description)
    if (form.amount)      fd.append('amount',      form.amount)

    if (type === 'refuel') {
      fd.append('fuel_type', form.fuel_type)
      if (form.fuel_liters)        fd.append('fuel_liters',       form.fuel_liters)
      if (form.station_name)       fd.append('station_name',      form.station_name)
      if (form.mileage_at_refuel)  fd.append('mileage_at_refuel', form.mileage_at_refuel)
      fd.append('is_full_tank', form.is_full_tank ? 'true' : 'false')
    }
    if (type === 'wash') {
      fd.append('wash_type', form.wash_type)
    }
    if (type === 'maintenance') {
      if (form.maintenance_type)  fd.append('maintenance_type',  form.maintenance_type)
      if (form.service_name)      fd.append('service_name',      form.service_name)
      if (form.mileage_at_service) fd.append('mileage_at_service', form.mileage_at_service)
    }

    if (photos.scene)    fd.append('file',          photos.scene)
    if (photos.odometer) fd.append('file_odometer', photos.odometer)
    if (photos.receipt)  fd.append('file_receipt',  photos.receipt)

    try {
      const { data } = await api.post(`/trips/${tripId}/events`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      onAdded(data.data)
      onClose()
    } catch (e) {
      setErr(e.response?.data?.message || 'Ошибка добавления события')
    } finally { setLoading(false) }
  }

  const et = EVENT_TYPES.find(e => e.value === type)

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
      display: 'flex', alignItems: 'flex-end', zIndex: 200 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: '100%', maxWidth: 480, margin: '0 auto',
        background: 'var(--bg-card)', borderRadius: 'var(--r-xl) var(--r-xl) 0 0',
        padding: 'var(--gap)',
        paddingBottom: 'calc(var(--gap) + env(safe-area-inset-bottom, 0px))',
        maxHeight: '92vh', overflowY: 'auto',
      }}>
        {/* Заголовок */}
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 'var(--gap)' }}>
          <h3>Добавить событие</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none',
            color: 'var(--text-muted)', fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
        </div>

        {/* Тип события */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: 'var(--gap-sm)', marginBottom: 'var(--gap)' }}>
          {EVENT_TYPES.map(ev => (
            <button key={ev.value} type="button" onClick={() => switchType(ev.value)} style={{
              padding: 'var(--gap-sm)',
              border: `2px solid ${type === ev.value ? ev.color : 'var(--border)'}`,
              borderRadius: 'var(--r)',
              background: type === ev.value ? `${ev.color}18` : 'transparent',
              cursor: 'pointer', display: 'flex', flexDirection: 'column',
              alignItems: 'center', gap: 4, transition: 'all 0.15s',
            }}>
              <span style={{ fontSize: '1.5rem' }}>{ev.icon}</span>
              <span style={{
                fontSize: '0.82rem', fontWeight: type === ev.value ? 700 : 400,
                color: type === ev.value ? ev.color : 'var(--text)',
              }}>{ev.label}</span>
            </button>
          ))}
        </div>

        <div className="flex-col" style={{ gap: 'var(--gap-sm)' }}>
          {/* Сумма — всегда */}
          <div className="field" style={{ margin: 0 }}>
            <label className="field__label" style={{ fontSize: '0.78rem' }}>
              Сумма (₽)
              {type === 'refuel' && <span className="text-muted" style={{ fontWeight:400, marginLeft:4 }}>за топливо</span>}
            </label>
            <input className="input" type="number" placeholder="0"
              value={form.amount} onChange={set('amount')}
              style={{ fontSize: '1.1rem', fontWeight: 600 }} />
          </div>

          {/* Типоспецифичные поля */}
          {type === 'refuel'      && <RefuelForm      form={form} set={set} photos={photos} setPhoto={setPhoto} suggestedMileage={suggestedMileage} />}
          {type === 'wash'        && <WashForm        form={form} set={set} photos={photos} setPhoto={setPhoto} />}
          {type === 'maintenance' && <MaintenanceForm form={form} set={set} photos={photos} setPhoto={setPhoto} />}
          {type === 'other'       && <OtherForm       photos={photos} setPhoto={setPhoto} />}

          {/* Описание — всегда внизу */}
          <div className="field" style={{ margin: 0 }}>
            <label className="field__label" style={{ fontSize: '0.78rem' }}>Комментарий</label>
            <textarea className="input" rows={2} placeholder="Дополнительные сведения..."
              value={form.description} onChange={set('description')}
              style={{ resize: 'vertical' }} />
          </div>

          {err && <div className="alert alert--error">{err}</div>}

          <button className="btn btn--primary btn--full" style={{ height: 50, fontSize: '0.95rem' }}
            onClick={submit} disabled={loading}>
            {loading ? <span className="spinner" /> : `${et?.icon} Сохранить событие`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Карточка события ──────────────────────────────────────────────────────────
function EventCard({ ev }) {
  const et = EVENT_TYPES.find(t => t.value === ev.event_type)
  const [expanded, setExpanded] = useState(false)

  const subtitle = []
  if (ev.fuel_liters) subtitle.push(`${ev.fuel_liters} л · ${ev.fuel_type_label || ev.fuel_type}`)
  if (ev.wash_type_label) subtitle.push(ev.wash_type_label)
  if (ev.maintenance_type_label) subtitle.push(ev.maintenance_type_label)
  if (ev.station_name || ev.details?.station_name) subtitle.push(ev.station_name || ev.details?.station_name)
  if (ev.service_name || ev.details?.service_name) subtitle.push(ev.service_name || ev.details?.service_name)
  if (ev.description) subtitle.push(ev.description)

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}
      onClick={() => ev.files?.length > 0 && setExpanded(e => !e)}>
      <div style={{ display: 'flex', gap: 'var(--gap-sm)', padding: 'var(--gap-sm) var(--gap)',
        alignItems: 'flex-start' }}>
        {/* Иконка */}
        <div style={{
          width: 40, height: 40, borderRadius: 'var(--r-sm)', flexShrink: 0,
          background: `${et?.color}18`, display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem',
        }}>
          {et?.icon}
        </div>

        {/* Текст */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={{ fontWeight: 700, fontSize: '0.9rem' }}>{ev.event_label}</p>
            {ev.amount > 0 && (
              <span style={{ fontWeight: 700, fontSize: '0.95rem', color: et?.color || 'var(--warning)' }}>
                {ev.amount.toLocaleString()} ₽
              </span>
            )}
          </div>
          {subtitle.length > 0 && (
            <p className="text-muted" style={{ fontSize: '0.78rem', marginTop: 2,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {subtitle.join(' · ')}
            </p>
          )}
          <p className="text-muted" style={{ fontSize: '0.72rem', marginTop: 2 }}>
            {new Date(ev.created_at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
            {ev.files?.length > 0 && ` · ${ev.files.length} фото`}
          </p>
        </div>

        {/* Превью первого фото */}
        {ev.files?.[0] && !expanded && (
          <img src={ev.files[0]} alt=""
            style={{ width: 48, height: 48, objectFit: 'cover',
              borderRadius: 'var(--r-sm)', flexShrink: 0,
              border: '1px solid var(--border)' }} />
        )}
      </div>

      {/* Развёрнутые фото */}
      {expanded && ev.files?.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(ev.files.length, 3)}, 1fr)`,
          gap: 2, padding: '0 2px 2px' }}>
          {ev.files.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
              <img src={url} alt={`фото ${i+1}`}
                style={{ width: '100%', height: 100, objectFit: 'cover',
                  borderRadius: i === 0 ? '0 0 0 var(--r-sm)' : i === ev.files.length - 1 ? '0 0 var(--r-sm) 0' : 0 }} />
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Сводка расходов ───────────────────────────────────────────────────────────
function ExpenseSummary({ events }) {
  const byType = {}
  let total = 0
  events.forEach(ev => {
    if (ev.amount > 0) {
      byType[ev.event_type] = (byType[ev.event_type] || 0) + ev.amount
      total += ev.amount
    }
  })
  if (total === 0) return null

  const entries = Object.entries(byType)
  return (
    <div className="card" style={{
      background: 'rgba(59,130,246,0.05)',
      border: '1px solid rgba(59,130,246,0.2)',
    }}>
      <p style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>💰 Расходы в поездке</p>
      {entries.map(([type, amt]) => {
        const et = EVENT_TYPES.find(e => e.value === type)
        return (
          <div key={type} style={{ display: 'flex', justifyContent: 'space-between',
            padding: '3px 0', fontSize: '0.82rem' }}>
            <span className="text-muted">{et?.icon} {et?.label}</span>
            <span style={{ fontWeight: 600 }}>{amt.toLocaleString()} ₽</span>
          </div>
        )
      })}
      {entries.length > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between',
          paddingTop: 6, marginTop: 4, borderTop: '1px solid var(--border)',
          fontWeight: 700, fontSize: '0.88rem' }}>
          <span>Итого</span>
          <span style={{ color: 'var(--accent)' }}>{total.toLocaleString()} ₽</span>
        </div>
      )}
    </div>
  )
}

// ── Экран активной поездки ────────────────────────────────────────────────────
export default function ActiveTripScreen({ trip, car, onFinish }) {
  const [events, setEvents]       = useState(trip.events || [])
  const [showEvent, setShowEvent] = useState(false)
  // Водитель должен видеть, что его место записывают, — и почему
  // точки может не быть. Скрытый трекинг доверие к продукту не переживёт.
  const { source: geoSource, accuracy: geoAccuracy } = useGeoStatus()
  // Трек копится и уезжает пачками через офлайн-очередь.
  useTripGeo(trip)

  const elapsed = () => {
    const s   = trip.started_at
    const norm = /[Z+]/.test(s) ? s : s + 'Z'
    const ms  = Math.max(0, Date.now() - new Date(norm).getTime())
    const h   = Math.floor(ms / 3_600_000)
    const m   = Math.floor((ms % 3_600_000) / 60_000)
    return h > 0 ? `${h} ч ${m} мин` : `${m} мин`
  }

  return (
    <>
      <div className="p-page flex-col" style={{ gap: 'var(--gap)', paddingTop: 'var(--gap)' }}>

        <GeoStatus source={geoSource} accuracy={geoAccuracy} />

        {/* Статус-карточка */}
        <div style={{
          background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.3)',
          borderRadius: 'var(--r)', padding: 'var(--gap)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ fontSize: '0.75rem', color: 'var(--accent)', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
                🚗 ПОЕЗДКА АКТИВНА
              </p>
              <p style={{ fontSize: '1.1rem', fontWeight: 700 }}>{car?.brand} {car?.model}</p>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2 }}>{car?.plate}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--accent)', lineHeight: 1 }}>
                {elapsed()}
              </p>
              <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>в пути</p>
            </div>
          </div>
          <div style={{ marginTop: 'var(--gap-sm)', paddingTop: 'var(--gap-sm)',
            borderTop: '1px solid rgba(59,130,246,0.2)',
            display: 'flex', justifyContent: 'space-between' }}>
            <span className="text-muted" style={{ fontSize: '0.82rem' }}>Начало пробега</span>
            <span style={{ fontWeight: 600 }}>{trip.start_mileage?.toLocaleString()} км</span>
          </div>
        </div>

        {/* Кнопка завершить */}
        <button onClick={onFinish} style={{
          width: '100%', height: 54, background: 'var(--danger)', color: '#fff',
          border: 'none', borderRadius: 'var(--r)', cursor: 'pointer',
          fontSize: '1rem', fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          🏁 Завершить поездку
        </button>

        {/* Сводка расходов */}
        <ExpenseSummary events={events} />

        {/* Быстрые кнопки событий */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
          {EVENT_TYPES.map(et => (
            <button key={et.value} type="button"
              onClick={() => setShowEvent(et.value)}
              style={{
                padding: '10px 4px', border: `1px solid ${et.color}40`,
                borderRadius: 'var(--r)', background: `${et.color}0d`,
                cursor: 'pointer', display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 4, transition: 'all 0.15s',
              }}>
              <span style={{ fontSize: '1.4rem' }}>{et.icon}</span>
              <span style={{ fontSize: '0.68rem', color: et.color, fontWeight: 600, textAlign: 'center' }}>
                {et.label}
              </span>
            </button>
          ))}
        </div>

        {/* Список событий */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p className="section-title" style={{ margin: 0 }}>
            События {events.length > 0 && `(${events.length})`}
          </p>
        </div>

        {events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--gap)',
            color: 'var(--text-muted)', fontSize: '0.85rem' }}>
            <p>Нажмите на кнопку выше чтобы добавить заправку, мойку или другое событие</p>
          </div>
        ) : (
          <div className="flex-col" style={{ gap: 'var(--gap-sm)' }}>
            {events.map(ev => <EventCard key={ev.id} ev={ev} />)}
          </div>
        )}
      </div>

      {showEvent && (
        <EventModal
          tripId={trip.id}
          initialType={typeof showEvent === 'string' ? showEvent : 'refuel'}
          suggestedMileage={trip.start_mileage}
          onClose={() => setShowEvent(false)}
          onAdded={ev => setEvents(e => [...e, ev])}
        />
      )}
    </>
  )
}
